import { useEffect, useState } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import { DA, SunOrnament, SabrilDecorFrame } from '../theme/sabril-da';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Config per offer — Affaires vs Première (unified DA palette)
// ─────────────────────────────────────────────────────────────
const OFFER_CONFIG = {
  affaires: {
    accent: DA.goldMain,
    accentDark: DA.goldDeep,
    accentGlow: 'rgba(216,148,64,0.55)',
    accentBg: DA.goldSofter,
    kicker: '90J · DISTANCIEL',
    title: 'Devis sur-mesure',
    subtitle: 'Pilotage rapproché 90 jours · WhatsApp prioritaire · adapté à ton contexte.',
    submitDirectLabel: 'Recevoir mon devis sous 24h',
    submitCafeLabel: 'Café de calibrage d\'abord',
    forceCafe: false,
    showAffairesFields: true,
    showPremiereFields: false,
  },
  premiere: {
    accent: DA.goldMain,
    accentDark: DA.goldDeep,
    accentGlow: 'rgba(216,148,64,0.55)',
    accentBg: DA.goldSofter,
    kicker: 'PREMIÈRE CLASSE · 7J + 10J SUR PLACE',
    title: 'Étape 1/2 · On se pose',
    subtitle: '45min de café pour cadrer le projet ensemble — je te livre ton devis 48h après.',
    submitDirectLabel: 'Réserver mon café de calibrage',
    submitCafeLabel: 'Réserver mon café de calibrage',
    forceCafe: true,
    showAffairesFields: false,
    showPremiereFields: true,
  },
};

const COUNTRIES = [
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'MA', label: '🇲🇦 Maroc' },
  { code: 'ES', label: '🇪🇸 Espagne' },
];

const STATUS_OPTIONS = [
  { code: 'existing', label: 'Déjà ouvert', sub: 'En activité' },
  { code: 'opening',  label: 'Projet d\'ouverture', sub: 'À venir' },
];

const SITES_OPTIONS = [
  { code: '1',  label: '1 site' },
  { code: '2',  label: '2 sites' },
  { code: '3+', label: '3+ sites' },
];

const CA_OPTIONS = [
  { code: '<30k',    label: '< 30k€' },
  { code: '30-80k',  label: '30-80k€' },
  { code: '80-150k', label: '80-150k€' },
  { code: '150k+',   label: '150k+€' },
  { code: 'projet',  label: 'En projet' },
];

const PROJECT_TYPE_OPTIONS = [
  { code: 'refonte',          label: 'Refonte totale', sub: 'Resto existant' },
  { code: 'opening_scratch',  label: 'Ouverture', sub: 'From scratch' },
];

const SURFACE_OPTIONS = [
  { code: '<150',    label: '< 150 m²' },
  { code: '150-300', label: '150-300 m²' },
  { code: '300+',    label: '300+ m²' },
];

// Same 12 chantiers as backend CHANTIER_LIST
const CHANTIERS = [
  { num: '00', label: 'Coaching dirigeant', icon: '🧭' },
  { num: '01', label: 'ADN & histoire de marque', icon: '🧬' },
  { num: '02', label: 'Expérience au sein du lieu', icon: '🎭' },
  { num: '03', label: 'Design d\'espace', icon: '🏛️' },
  { num: '04', label: 'Menu gagnant', icon: '📖' },
  { num: '05', label: 'Food cost & marges', icon: '💰' },
  { num: '06', label: 'RH & performance équipe', icon: '👥' },
  { num: '07', label: 'Livraison & plateformes', icon: '🛵' },
  { num: '08', label: 'Packaging', icon: '📦' },
  { num: '09', label: 'Merch & extension', icon: '👕' },
  { num: '10', label: 'DA & Calendrier éditorial', icon: '📸' },
  { num: '11', label: 'Opening · Présence sur place', icon: '🎬' },
];

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  restaurant_name: '',
  address: '',
  city: '',
  country: 'FR',
  status: 'existing',
  preferred_date: '',
  nb_sites: '1',
  ca_range: '30-80k',
  project_type: 'refonte',
  surface: '150-300',
  concept: '',
  priorities: [],
  notes: '',
  contact_mode: 'direct_quote',
};

/**
 * QuoteRequestModal — Bespoke quote modal for Classe Affaires + Première Classe.
 * Listens to global events `affaires:open` and `premiere:open`.
 */
export default function QuoteRequestModal() {
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState('affaires');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    const openAffaires = () => {
      setOffer('affaires');
      setForm({ ...emptyForm, contact_mode: 'direct_quote' });
      setError(''); setConfirmed(false);
      setOpen(true);
    };
    const openPremiere = () => {
      setOffer('premiere');
      // Première is always café-first
      setForm({ ...emptyForm, contact_mode: 'cafe_first' });
      setError(''); setConfirmed(false);
      setOpen(true);
    };
    window.addEventListener('affaires:open', openAffaires);
    window.addEventListener('premiere:open', openPremiere);
    return () => {
      window.removeEventListener('affaires:open', openAffaires);
      window.removeEventListener('premiere:open', openPremiere);
    };
  }, []);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [open]);

  const cfg = OFFER_CONFIG[offer] || OFFER_CONFIG.affaires;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const togglePriority = (num) => {
    setForm((f) => {
      const has = f.priorities.includes(num);
      return {
        ...f,
        priorities: has ? f.priorities.filter((p) => p !== num) : [...f.priorities, num],
      };
    });
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setError('');
    // Basic validation
    const required = ['firstName', 'lastName', 'email', 'phone', 'restaurant_name', 'address', 'city'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        setError('Remplis tous les champs de contact pour continuer.');
        return;
      }
    }
    if (!form.email.includes('@')) {
      setError('Email invalide.');
      return;
    }
    if (form.priorities.length === 0) {
      setError('Sélectionne au moins 1 chantier prioritaire.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/quote/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer, ...form }),
      });
      const data = await res.json();
      if (res.ok && data && data.id) {
        setConfirmed(true);
        setLoading(false);
        return;
      }
      setError('Impossible d\'envoyer ta demande. Réessaie ou contacte Sabril par WhatsApp.');
    } catch (err) {
      setError('Erreur réseau. Réessaie.');
    }
    setLoading(false);
  };

  if (!open) return null;

  // ────── Confirmation screen ──────
  if (confirmed) {
    const isCafe = cfg.forceCafe || form.contact_mode === 'cafe_first';
    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) { setOpen(false); setConfirmed(false); } }}
        style={{ background: 'rgba(11,43,36,0.55)', backdropFilter: 'blur(8px)' }}
        data-testid={`quote-modal-confirmed-${offer}`}
      >
        <div
          className="w-full sm:max-w-[440px] p-6 sm:p-8 text-center"
          style={{
            background: DA.ivory,
            borderRadius: '24px 24px 0 0',
            color: DA.greenDeep,
          }}
        >
          <div className="flex justify-center mb-4">
            <SunOrnament size={28} />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 mx-auto"
               style={{ background: DA.goldSofter, border: `1px solid ${DA.goldMain}` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: DA.goldMain }} />
            <span style={{ color: DA.goldDeep, fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Demande reçue</span>
          </div>
          <h2 style={{ fontFamily: DA.serif, fontSize: 28, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 12, color: DA.greenDeep }}>
            Bien reçu <span style={{ color: DA.goldMain }}>{form.firstName}</span>.
          </h2>
          <p style={{ color: DA.inkMuted, fontSize: 13.5, lineHeight: 1.45, marginBottom: 24, fontFamily: DA.sans }}>
            {isCafe
              ? `Je te contacte sous 24h par WhatsApp pour caler notre café de calibrage sur ${form.restaurant_name}. Après le café, tu reçois ton devis sous 48h.`
              : `Je reviens vers toi sous 24h par WhatsApp avec un devis sur-mesure aligné sur les ${form.priorities.length} chantier${form.priorities.length > 1 ? 's' : ''} que tu as sélectionné${form.priorities.length > 1 ? 's' : ''}.`}
          </p>
          <div className="rounded-2xl px-4 py-3 mb-5 text-left" style={{ background: DA.ivoryDeep, border: `0.5px solid ${DA.goldSoft}` }}>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', color: DA.goldMain, fontFamily: DA.sans }}>Récapitulatif</p>
            <p style={{ color: DA.ink, fontSize: 13, marginTop: 8, lineHeight: 1.55, fontFamily: DA.sans }}>
              {cfg.kicker}<br/>
              <span style={{ color: DA.goldMain }}>📍</span> {form.restaurant_name} · {form.city}<br/>
              <span style={{ color: DA.goldMain }}>🎯</span> {form.priorities.length} chantier{form.priorities.length > 1 ? 's' : ''} prioritaire{form.priorities.length > 1 ? 's' : ''}<br/>
              <span style={{ color: DA.goldMain }}>📞</span> {isCafe ? 'Café de calibrage d\'abord' : 'Devis direct 24h'}
            </p>
          </div>
          <p style={{ color: DA.inkFaint, fontSize: 11, marginBottom: 16, lineHeight: 1.55, fontFamily: DA.sans }}>
            💡 Pendant ce temps, prépare 3 photos de ton lieu, ta carte et ton dernier P&amp;L. Ça accélère.
          </p>
          <button
            type="button"
            onClick={() => { setOpen(false); setConfirmed(false); }}
            className="w-full py-3.5 rounded-xl font-bold transition active:scale-[0.98]"
            style={{
              background: DA.greenDeep,
              color: '#FFFFFF',
              fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
              fontFamily: DA.sans,
              boxShadow: `0 12px 28px -8px ${DA.greenDeep}88`,
            }}
            data-testid={`quote-confirmed-close-${offer}`}
          >
            J&apos;ai compris — à bientôt
          </button>
        </div>
      </div>
    );
  }

  // ────── Form ──────
  const inputStyle = {
    width: '100%',
    padding: '11px 13px',
    background: '#FFFFFF',
    border: '1px solid rgba(0,0,0,0.10)',
    borderRadius: 10,
    fontSize: 14.5,
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    color: '#1a1a1a',
    outline: 'none',
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5A5A5A',
    marginBottom: 6,
    display: 'block',
  };

  const chip = (opt, activeCode, onClick) => (
    <button
      key={opt.code}
      type="button"
      onClick={onClick}
      className="text-left transition active:scale-[0.98]"
      style={{
        padding: '10px 12px',
        borderRadius: 12,
        border: activeCode === opt.code ? `2px solid ${cfg.accent}` : '1px solid rgba(0,0,0,0.10)',
        background: activeCode === opt.code ? cfg.accentBg : '#FFFFFF',
      }}
    >
      <p style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>{opt.label}</p>
      {opt.sub && (
        <p style={{ fontSize: 10.5, color: '#5A5A5A', marginTop: 2 }}>{opt.sub}</p>
      )}
    </button>
  );

  const submitLabel = (cfg.forceCafe || form.contact_mode === 'cafe_first')
    ? cfg.submitCafeLabel
    : cfg.submitDirectLabel;
  const isCafe = cfg.forceCafe || form.contact_mode === 'cafe_first';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      data-testid={`quote-modal-${offer}`}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{ background: 'rgba(11,43,36,0.55)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full sm:max-w-[480px] max-h-[92vh] overflow-y-auto relative"
        style={{
          background: DA.ivory,
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -20px 60px -10px rgba(11,43,36,0.35)',
        }}
      >
        <SabrilDecorFrame awningTL awningBR={false} mimosa={false} palm awningSize={90} />
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 pt-4 pb-3 flex items-start justify-between"
             style={{ background: `linear-gradient(180deg, ${DA.ivory} 92%, transparent 100%)` }}>
          <div className="flex-1 pr-3">
            <p style={{
              fontFamily: DA.sans, fontSize: 9.5,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: DA.goldMain, fontWeight: 800,
            }}>
              {cfg.kicker}
            </p>
            <h2 style={{
              fontFamily: DA.serif,
              fontSize: 26, fontWeight: 700,
              letterSpacing: '-0.02em', lineHeight: 1.05,
              color: DA.greenDeep, marginTop: 4,
            }}>
              {cfg.title}<span style={{ color: DA.goldMain }}>.</span>
            </h2>
            <p style={{ fontSize: 13, color: DA.inkMuted, marginTop: 6, lineHeight: 1.4, fontFamily: DA.sans }}>
              {cfg.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-full active:scale-95 transition flex-shrink-0"
            style={{ background: DA.ivoryDeep, color: DA.greenDeep, border: `0.5px solid ${DA.goldSoft}` }}
            aria-label="Fermer"
            data-testid={`quote-modal-close-${offer}`}
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-5 pb-8 space-y-4" data-testid={`quote-form-${offer}`}>

          {/* SECTION 1 : Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Prénom</label>
              <input type="text" value={form.firstName} onChange={set('firstName')} style={inputStyle} placeholder="Prénom" required data-testid="quote-firstName" />
            </div>
            <div>
              <label style={labelStyle}>Nom</label>
              <input type="text" value={form.lastName} onChange={set('lastName')} style={inputStyle} placeholder="Nom" required data-testid="quote-lastName" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.email} onChange={set('email')} style={inputStyle} placeholder="ton@email.com" required data-testid="quote-email" />
          </div>
          <div>
            <label style={labelStyle}>Téléphone (WhatsApp)</label>
            <input type="tel" value={form.phone} onChange={set('phone')} style={inputStyle} placeholder="+33 6 12 34 56 78" required data-testid="quote-phone" />
          </div>

          {/* SECTION 2 : Restaurant */}
          <div>
            <label style={labelStyle}>Nom du restaurant</label>
            <input type="text" value={form.restaurant_name} onChange={set('restaurant_name')} style={inputStyle} placeholder="Ex : Maison Avocado" required data-testid="quote-restaurant" />
          </div>
          <div>
            <label style={labelStyle}>Adresse</label>
            <input type="text" value={form.address} onChange={set('address')} style={inputStyle} placeholder="Rue, avenue…" required data-testid="quote-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Ville</label>
              <input type="text" value={form.city} onChange={set('city')} style={inputStyle} placeholder="Ville" required data-testid="quote-city" />
            </div>
            <div>
              <label style={labelStyle}>Pays</label>
              <select value={form.country} onChange={set('country')} style={{ ...inputStyle, appearance: 'auto' }} data-testid="quote-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Statut */}
          <div>
            <label style={labelStyle}>Statut du restaurant</label>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((s) => chip(s, form.status, () => setForm((f) => ({ ...f, status: s.code }))))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label style={labelStyle}>Date de démarrage souhaitée</label>
            <input type="date" value={form.preferred_date} onChange={set('preferred_date')} min={new Date().toISOString().split('T')[0]} style={inputStyle} data-testid="quote-date" />
          </div>

          {/* SECTION 3 : Affaires-specific */}
          {cfg.showAffairesFields && (
            <>
              <div>
                <label style={labelStyle}>Nombre d&apos;établissements</label>
                <div className="grid grid-cols-3 gap-2">
                  {SITES_OPTIONS.map((s) => chip(s, form.nb_sites, () => setForm((f) => ({ ...f, nb_sites: s.code }))))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>CA mensuel actuel</label>
                <div className="grid grid-cols-3 gap-2">
                  {CA_OPTIONS.slice(0,3).map((s) => chip(s, form.ca_range, () => setForm((f) => ({ ...f, ca_range: s.code }))))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {CA_OPTIONS.slice(3).map((s) => chip(s, form.ca_range, () => setForm((f) => ({ ...f, ca_range: s.code }))))}
                </div>
              </div>
            </>
          )}

          {/* SECTION 3bis : Première-specific */}
          {cfg.showPremiereFields && (
            <>
              <div>
                <label style={labelStyle}>Type de projet</label>
                <div className="grid grid-cols-2 gap-2">
                  {PROJECT_TYPE_OPTIONS.map((s) => chip(s, form.project_type, () => setForm((f) => ({ ...f, project_type: s.code }))))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Surface du restaurant</label>
                <div className="grid grid-cols-3 gap-2">
                  {SURFACE_OPTIONS.map((s) => chip(s, form.surface, () => setForm((f) => ({ ...f, surface: s.code }))))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Nombre de sites concernés</label>
                <div className="grid grid-cols-3 gap-2">
                  {SITES_OPTIONS.map((s) => chip(s, form.nb_sites, () => setForm((f) => ({ ...f, nb_sites: s.code }))))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Concept / ADN (1-2 lignes)</label>
                <textarea
                  rows={2}
                  value={form.concept}
                  onChange={set('concept')}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                  placeholder="Ex : Trattoria signature avec bar à cocktails, terrasse, 60 couverts…"
                  data-testid="quote-concept"
                />
              </div>
            </>
          )}

          {/* SECTION 4 : 12 chantiers */}
          <div>
            <label style={labelStyle}>
              🎯 Chantiers prioritaires · coche ceux qui te concernent
            </label>
            <div className="grid grid-cols-1 gap-2">
              {CHANTIERS.map((c) => {
                const active = form.priorities.includes(c.num);
                return (
                  <button
                    key={c.num}
                    type="button"
                    onClick={() => togglePriority(c.num)}
                    className="flex items-center gap-3 text-left transition active:scale-[0.99]"
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: active ? `2px solid ${cfg.accent}` : '1px solid rgba(0,0,0,0.10)',
                      background: active ? cfg.accentBg : '#FFFFFF',
                    }}
                    data-testid={`quote-chantier-${c.num}`}
                  >
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 26, height: 26, borderRadius: 8,
                        background: active ? cfg.accent : 'rgba(0,0,0,0.05)',
                        color: active ? '#FFFFFF' : '#1a1a1a',
                        fontSize: 14,
                      }}
                    >
                      {active ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : c.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                        {c.label}
                      </p>
                    </div>
                    <span style={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                      color: active ? cfg.accentDark : '#9CA3AF',
                    }}>
                      #{c.num}
                    </span>
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: '#5A5A5A', marginTop: 6, lineHeight: 1.4 }}>
              📊 {form.priorities.length}/12 sélectionné{form.priorities.length > 1 ? 's' : ''}
            </p>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>📝 Notes / contexte libre (optionnel)</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={set('notes')}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
              placeholder="Décris ton contexte, tes urgences, ton ambition en quelques lignes…"
              data-testid="quote-notes"
            />
          </div>

          {/* Mode de contact — Affaires only (Première is forced to café-first) */}
          {!cfg.forceCafe && (
            <div>
              <label style={labelStyle}>Mode de contact préféré</label>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, contact_mode: 'direct_quote' }))}
                  className="text-left transition active:scale-[0.99]"
                  style={{
                    padding: '11px 12px',
                    borderRadius: 12,
                    border: form.contact_mode === 'direct_quote' ? `2px solid ${cfg.accent}` : '1px solid rgba(0,0,0,0.10)',
                    background: form.contact_mode === 'direct_quote' ? cfg.accentBg : '#FFFFFF',
                  }}
                  data-testid="quote-contact-direct"
                >
                  <p style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                    📩 Envoie-moi directement le devis sous 24h
                  </p>
                  <p style={{ fontSize: 11, color: '#5A5A5A', marginTop: 2 }}>
                    Recommandé si tu es déjà décidé sur le périmètre
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, contact_mode: 'cafe_first' }))}
                  className="text-left transition active:scale-[0.99]"
                  style={{
                    padding: '11px 12px',
                    borderRadius: 12,
                    border: form.contact_mode === 'cafe_first' ? `2px solid ${cfg.accent}` : '1px solid rgba(0,0,0,0.10)',
                    background: form.contact_mode === 'cafe_first' ? cfg.accentBg : '#FFFFFF',
                  }}
                  data-testid="quote-contact-cafe"
                >
                  <p style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                    📞 Café de calibrage d&apos;abord
                  </p>
                  <p style={{ fontSize: 11, color: '#5A5A5A', marginTop: 2 }}>
                    45min pour cadrer ensemble avant devis
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Info banner (Première only) */}
          {cfg.forceCafe && (
            <div className="rounded-xl px-4 py-3" style={{ background: cfg.accentBg, border: `1px solid ${cfg.accent}30` }}>
              <p style={{ fontSize: 12, fontWeight: 800, color: cfg.accentDark, letterSpacing: '-0.005em', marginBottom: 3 }}>
                Étape 1/2 · Café de calibrage (45min)
              </p>
              <p style={{ fontSize: 11.5, color: '#4A5A5A', lineHeight: 1.4 }}>
                Un projet Première Classe se cadre en présentiel avant toute chiffrage. Le café est ma manière de comprendre ton contexte, faire un premier repérage, et te livrer un devis précis sous 48h.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <p style={{ color: '#DC2626', fontSize: 12.5, fontWeight: 700 }} data-testid={`quote-error-${offer}`}>
              {error}
            </p>
          )}

          {/* CTA */}
          <button
            type="submit"
            disabled={loading}
            className="w-full active:scale-[0.98] transition disabled:opacity-70 flex items-center justify-center gap-2"
            style={{
              background: DA.greenDeep,
              color: '#FFFFFF',
              padding: '15px 20px',
              borderRadius: 12,
              fontFamily: DA.sans,
              fontSize: 13, fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              boxShadow: `0 12px 28px -8px ${DA.greenDeep}88`,
              border: 'none',
              cursor: loading ? 'default' : 'pointer',
              marginTop: 4,
            }}
            data-testid={`quote-submit-${offer}`}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</>
            ) : (
              <><span style={{ color: DA.goldMain }}>{isCafe ? '📞' : '📩'}</span> {submitLabel} →</>
            )}
          </button>

          <p style={{ fontSize: 10.5, color: DA.inkMuted, textAlign: 'center', lineHeight: 1.4, fontFamily: DA.sans }}>
            🔒 Aucun paiement maintenant · Sabril te contacte {cfg.forceCafe || form.contact_mode === 'cafe_first' ? 'sous 24h par WhatsApp pour caler le café' : 'sous 24h avec un devis sur-mesure'}.
          </p>
        </form>
      </div>
    </div>
  );
}
